import * as Portal from '@radix-ui/react-portal';
import { ReactNode, useState } from 'react';
import { Link } from 'react-router';
import { useSnapshot } from 'valtio';

import * as authStore from '@cartwhl/common/stores/authStore';
import { getAppletsUrl } from '@cartwhl/common/utils';
import { getHomepageUrl } from '@cartwhl/common/utils/getHomepageUrl';
import Text from '@cartwhl/ui/base/TextNew';

import ChevronLeftIcon from '../icons/ChevronLeftIcon';
import CrossIcon from '../icons/CrossIcon';
import Button from '../input/Button';
import './MobileMenu.css';

// Check if we're on the homepage origin (client-side nav works)
// vs another app like applets (need full page navigation)
const isOnHomepage = getHomepageUrl('/') === `${window.location.origin}/`;

function MobileMenuLink({
  to,
  className,
  children,
  onClick,
}: {
  to: string;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
}) {
  if (isOnHomepage) {
    return (
      <Link to={to} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  }
  return (
    <a href={getHomepageUrl(to)} className={className} onClick={onClick}>
      {children}
    </a>
  );
}

interface MenuItem {
  title: string;
  subtitle?: string;
  href: string;
  external?: boolean;
  comingSoonText?: string;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

interface MobileMenuProps {
  productSections: MenuSection[];
  pricingItems?: MenuItem[];
  learnItems?: MenuItem[];
  onComingSoonClick?: (slug?: string) => void;
}

type LinkLists = 'default' | 'products' | 'pricing' | 'learn';

export default function MobileMenu({
  productSections,
  pricingItems,
  learnItems,
  onComingSoonClick,
}: MobileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showLinks, setShowLinks] = useState<LinkLists>('default');

  const { authStatus } = useSnapshot(authStore.state);
  const isSignedIn = authStatus === 'user';

  const openMenu = () => {
    setIsOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeMenu = () => {
    setIsOpen(false);
    document.body.style.overflow = '';
  };
  return (
    <>
      {/* Menu trigger button - only visible below lg breakpoint (1024px) */}
      <button
        onClick={openMenu}
        className="flex items-center lg:hidden"
        aria-label="Open menu"
      >
        <Text as="span" token="16/medium">
          Menu
        </Text>
      </button>

      {isOpen && (
        <Portal.Root
          className="z-100 fixed left-0 right-0 top-0 bg-green-50/90 text-white backdrop-blur-[10px]"
          style={{ height: '100svh' }}
        >
          {/* Back button */}
          {showLinks !== 'default' && (
            <button
              onClick={() => setShowLinks('default')}
              className="absolute left-5 top-4 p-3"
            >
              <ChevronLeftIcon />
            </button>
          )}

          {/* Close button */}
          <button onClick={closeMenu} className="absolute right-5 top-4 p-3">
            <CrossIcon />
          </button>

          {showLinks === 'default' && (
            <div className="container flex flex-col gap-6 py-4 pt-20 text-[32px] font-medium leading-normal">
              <div className="menu-item-animated">
                <button
                  onClick={() => setShowLinks('products')}
                  className="text-left"
                >
                  Product
                </button>
              </div>
              <MobileMenuLink
                to="/plugins"
                className="menu-item-animated"
                onClick={closeMenu}
              >
                Plugins
              </MobileMenuLink>
              <MobileMenuLink
                to="/data"
                className="menu-item-animated"
                onClick={closeMenu}
              >
                Data
              </MobileMenuLink>
              {/* <MobileMenuLink
                className="menu-item-animated"
                to="/blog"
                onClick={closeMenu}
              >
                Blog
              </MobileMenuLink> */}
              {pricingItems ? (
                <button
                  onClick={() => setShowLinks('pricing')}
                  className="menu-item-animated text-left"
                >
                  Pricing
                </button>
              ) : (
                <MobileMenuLink
                  to="/pricing"
                  onClick={closeMenu}
                  className="menu-item-animated"
                >
                  Pricing
                </MobileMenuLink>
              )}
              <MobileMenuLink
                className="menu-item-animated"
                to="/company"
                onClick={closeMenu}
              >
                Company
              </MobileMenuLink>
              {learnItems ? (
                <button
                  onClick={() => setShowLinks('learn')}
                  className="menu-item-animated text-left"
                >
                  Learn
                </button>
              ) : (
                <MobileMenuLink
                  className="menu-item-animated"
                  to="/docs"
                  onClick={closeMenu}
                >
                  Learn
                </MobileMenuLink>
              )}
              {isSignedIn ? (
                <>
                  <MobileMenuLink
                    className="menu-item-animated"
                    to="/profile/overview"
                    onClick={closeMenu}
                  >
                    Profile
                  </MobileMenuLink>
                  <a
                    className="menu-item-animated text-gray-90"
                    href={getAppletsUrl()}
                    onClick={closeMenu}
                  >
                    Launch Cartwheel
                  </a>
                </>
              ) : (
                <>
                  <MobileMenuLink
                    className="menu-item-animated text-gray-90"
                    to="/login"
                    onClick={closeMenu}
                  >
                    Sign in
                  </MobileMenuLink>
                  <MobileMenuLink
                    className="menu-item-animated mt-16"
                    to="/pricing"
                    onClick={closeMenu}
                  >
                    <Button>Free trial</Button>
                  </MobileMenuLink>
                </>
              )}
            </div>
          )}

          {showLinks === 'products' && (
            <div className="container flex flex-col gap-8 py-4 pt-20">
              {productSections.map((section) => (
                <div key={section.title} className="flex flex-col gap-6">
                  <Text
                    as="span"
                    token="24/medium"
                    className="menu-item-animated text-white/70"
                  >
                    {section.title}
                  </Text>
                  <div className="flex flex-col gap-6 text-[32px] font-medium leading-normal tracking-[-0.96px] text-white">
                    {section.items.map((item) =>
                      item.comingSoonText ? (
                        <button
                          key={item.href}
                          className="menu-item-animated flex items-center gap-3 text-left"
                          onClick={() => {
                            onComingSoonClick?.(item.href.split('/').pop());
                            closeMenu();
                          }}
                        >
                          {item.title}
                          <span className="text-[14px]/[14px] text-white/60">
                            {item.comingSoonText}
                          </span>
                        </button>
                      ) : (
                        <MobileMenuLink
                          to={item.href}
                          key={item.href}
                          onClick={closeMenu}
                          className="menu-item-animated"
                        >
                          {item.title}
                        </MobileMenuLink>
                      ),
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {showLinks === 'pricing' && pricingItems && (
            <div className="container flex flex-col gap-6 py-4 pt-20 text-[32px] font-medium leading-normal">
              {pricingItems.map((item) =>
                item.external ? (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="menu-item-animated"
                    onClick={closeMenu}
                  >
                    {item.title}
                  </a>
                ) : (
                  <MobileMenuLink
                    to={item.href}
                    key={item.href}
                    onClick={closeMenu}
                    className="menu-item-animated"
                  >
                    {item.title}
                  </MobileMenuLink>
                ),
              )}
            </div>
          )}

          {showLinks === 'learn' && learnItems && (
            <div className="container flex flex-col gap-6 py-4 pt-20 text-[32px] font-medium leading-normal">
              {learnItems.map((item) =>
                item.external ? (
                  <a
                    href={item.href}
                    key={item.href}
                    className="menu-item-animated"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMenu}
                  >
                    {item.title}
                  </a>
                ) : (
                  <MobileMenuLink
                    to={item.href}
                    key={item.href}
                    className="menu-item-animated"
                    onClick={closeMenu}
                  >
                    {item.title}
                  </MobileMenuLink>
                ),
              )}
            </div>
          )}
        </Portal.Root>
      )}
    </>
  );
}
